import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import { TelemetryErrorBoundary as ErrorBoundary } from "@mixitone/telemetry";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type NodeOrFunction = React.ReactNode | (() => React.ReactNode);

type PanelProps = {
  title?: React.ReactNode;
  buttons?: NodeOrFunction;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  titleClassName?: string;
  hideTitle?: boolean;
};

const ErrorState = () => {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="-translate-y-5 text-center">
        <p className="mb-4 text-lg font-medium">Something went wrong</p>
        <p className="text-sm text-gray-600">Please try again later</p>
      </div>
    </div>
  );
};

export class PanelController extends ApplicationController<{ buttons: NodeOrFunction }, PanelProps> {
  static initialState = {
    buttons: null,
  };

  buttonsSet = false;
  buttonsFromProps?: NodeOrFunction;

  override async changeProps(newProps: PanelProps) {
    this.buttonsFromProps = newProps.buttons;

    if (!this.buttonsSet) {
      this.state.buttons = newProps.buttons;
    }
  }

  actionSetButtons(buttons: React.ReactNode) {
    if (buttons) {
      this.buttonsSet = true;
      this.state.buttons = buttons;
    } else {
      this.buttonsSet = false;
      this.state.buttons = this.buttonsFromProps;
    }
  }
}

const PanelComponent = ({
  title,
  titleClassName,
  className,
  internalClassName,
  style,
  children,
  hideTitle,
}: PanelProps & { internalClassName: string }) => {
  const controller = PanelController.use();
  const { buttons } = controller.state;
  const titleVisible = hideTitle !== true && title;
  const buttonsNode = typeof buttons === "function" ? buttons() : buttons;
  const showHeader = titleVisible || buttonsNode;

  return (
    <div
      className={twMerge(
        "panel group/panel rounded-lg bg-white shadow-sm relative",
        internalClassName,
        className,
      )}
      style={style}
      title={typeof title === "string" ? title : undefined}
    >
      <div className="flex h-full flex-col">
        {showHeader ? (
          <div
            className={clsx(
              "short:mb-2 medium:mb-4 flex items-center",
              titleVisible ? "justify-between" : "justify-end",
            )}
          >
            {titleVisible && <h2 className={clsx("text-lg font-medium", titleClassName)}>{title}</h2>}
            <div className="*:text-xs">{buttonsNode}</div>
          </div>
        ) : null}
        <ErrorBoundary fallback={<ErrorState />}>{children}</ErrorBoundary>
      </div>
    </div>
  );
};

const ControlledPanel = PanelController.scope(ApplicationView(PanelComponent));

export const Panel = (props: PanelProps) => {
  return <ControlledPanel internalClassName="short:p-4 medium:p-6 " {...props} />;
};

/** Single-panel page layout: flush on narrow app width, rounded card from @sm/app up. */
export const PagePanel = (props: PanelProps) => {
  return (
    <ControlledPanel
      internalClassName="short:p-4 medium:p-6 rounded-none shadow-none @sm/app:rounded-lg @sm/app:shadow-sm"
      {...props}
    />
  );
};

export const PanelFooter = ApplicationView(
  ({ children, className }: { children: React.ReactNode; className?: string }) => {
    const el = useRef<HTMLDivElement>(null);
    const [isIntersecting, setIsIntersecting] = useState(false);

    useEffect(() => {
      if (!el.current) return;
      const panel = el.current.closest(".panel");
      if (!panel) return;

      const observer = new IntersectionObserver((entries) => {
        if (entries.length === 0) setIsIntersecting(false);
        else setIsIntersecting(entries[0].isIntersecting);
      });

      observer.observe(panel);

      return () => {
        observer.disconnect();
      };
    }, []);

    useEffect(() => {
      if (!el.current) return;
      const panel = el.current.closest<HTMLDivElement>(".panel");
      if (!panel) return;

      // If panel is not visible, reset styles and do nothing else.
      if (!isIntersecting) {
        el.current.style.position = "";
        el.current.style.bottom = "";
        el.current.style.left = "";
        el.current.style.width = "";
        el.current.style.borderBottomLeftRadius = "";
        el.current.style.borderBottomRightRadius = "";
        return;
      }

      const scrollContainer = panel.closest(".scroll-container");
      if (!scrollContainer) return;

      const abort = new AbortController();
      const signal = abort.signal;

      // If the bottom of the panel is not visible then position the footer at the
      // bottom of the visible scroll area, otherwise position it at the bottom of the panel
      const positionFooter = () => {
        if (!el.current) return;
        const panelRect = panel.getBoundingClientRect();
        const scrollContainerRect = scrollContainer.getBoundingClientRect();

        panel.style.paddingBottom = `${el.current.offsetHeight + 16}px`;

        if (panelRect.bottom > scrollContainerRect.bottom && panelRect.top < scrollContainerRect.bottom) {
          el.current.style.position = "fixed";
          el.current.style.bottom = `${window.innerHeight - scrollContainerRect.bottom}px`;
          el.current.style.left = `${panelRect.left}px`;
          el.current.style.width = `${panelRect.width}px`;
          el.current.style.borderBottomLeftRadius = "0px";
          el.current.style.borderBottomRightRadius = "0px";
        } else {
          el.current.style.position = "";
          el.current.style.bottom = "";
          el.current.style.left = "";
          el.current.style.width = "";
          el.current.style.borderBottomLeftRadius = "";
          el.current.style.borderBottomRightRadius = "";
        }
      };

      scrollContainer.addEventListener("scroll", positionFooter, { signal });
      window.addEventListener("resize", positionFooter, { signal });

      const resizeObserver = new ResizeObserver(() => {
        positionFooter();
      });
      resizeObserver.observe(panel);
      resizeObserver.observe(scrollContainer);
      resizeObserver.observe(el.current);

      positionFooter();

      return () => {
        resizeObserver.disconnect();
        abort.abort();
      };
    }, [isIntersecting]);

    return (
      <div
        ref={el}
        className={clsx(
          "absolute bottom-0 left-0 right-0 p-6 flex items-center justify-end gap-x-6 bg-gray-50 border-t rounded-b-lg border-gray-200",
          className,
        )}
      >
        {children}
      </div>
    );
  },
);
